/**
 * SessionList — slide-over panel showing conversation history.
 * Renders inside ChatPanel when the session list is toggled open.
 * Shows both Talk sessions and agent conversations.
 */

import { Plus, Trash2, MessageSquare, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { TalkSessionInfo } from '../../api/talkSessions';

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface SessionListProps {
  sessions: TalkSessionInfo[];
  activeSessionId: string | null;
  isLoading: boolean;
  onSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onArchive: (sessionId: string) => void;
  onClose: () => void;
}

export function SessionList({
  sessions,
  activeSessionId,
  isLoading,
  onSelect,
  onNewSession,
  onArchive,
  onClose,
}: SessionListProps) {
  const navigate = useNavigate();
  const { wsSlug } = useWorkspace();

  const handleSessionClick = (session: TalkSessionInfo) => {
    if (session._source === 'agent') {
      // Navigate to the Sessions page detail view for agent conversations
      const base = wsSlug ? `/w/${wsSlug}` : '';
      navigate(`${base}/ar/se/${session.id}`);
      onClose();
    } else {
      onSelect(session.id);
      onClose();
    }
  };

  return (
    <div className="flex flex-col h-full animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
        <h4 className="text-sm font-semibold text-neutral-800">Conversations</h4>
        <button
          onClick={onClose}
          className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          Close
        </button>
      </div>

      {/* New conversation button */}
      <div className="px-3 pt-3 pb-1">
        <button
          onClick={() => { onNewSession(); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium
                     text-coral-700 bg-coral-50 hover:bg-coral-100
                     border border-coral-200/60 rounded-xl
                     transition-all duration-200 hover:shadow-warm-sm"
        >
          <Plus size={16} />
          New conversation
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-coral-300 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!isLoading && sessions.length === 0 && (
          <div className="text-center py-8 text-sm text-neutral-400">
            No previous conversations
          </div>
        )}

        {!isLoading && sessions.map((session) => {
          const isActive = session.id === activeSessionId;
          const isAgent = session._source === 'agent';
          return (
            <button
              key={session.id}
              onClick={() => handleSessionClick(session)}
              className={`w-full text-left group flex items-start gap-2.5 px-3 py-2.5 rounded-xl
                         transition-all duration-200 hover:bg-neutral-50
                         ${isActive ? 'bg-coral-50/60 border border-coral-200/40' : 'border border-transparent'}`}
            >
              {isAgent ? (
                <Bot size={14} className="mt-0.5 flex-shrink-0 text-indigo-400" />
              ) : (
                <MessageSquare
                  size={14}
                  className={`mt-0.5 flex-shrink-0 ${isActive ? 'text-coral-500' : 'text-neutral-300'}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate block ${isActive ? 'font-medium text-coral-800' : 'text-neutral-700'}`}>
                    {session.title || 'New conversation'}
                  </span>
                  {isAgent && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium flex-shrink-0">
                      Agent
                    </span>
                  )}
                  {!isAgent && (
                    <span className="text-[10px] text-neutral-400 flex-shrink-0 tabular-nums">
                      {session.message_count}
                    </span>
                  )}
                </div>
                <span className="text-[11px] text-neutral-400 block mt-0.5">
                  {relativeTime(session.updated_at)}
                </span>
              </div>
              {!isAgent && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(session.id);
                  }}
                  className="p-1 text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100
                             transition-all duration-200 rounded-lg hover:bg-red-50 flex-shrink-0 mt-0.5"
                  title="Archive conversation"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
